import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  FaHeart,
  FaCross,
  FaBible,
  FaUsers,
  FaArrowUp,
  FaYoutube,
  FaLaptopCode,
} from "react-icons/fa";
import hqWallpaper from "../../assets/hq-wallpaper.webp";
import logoCircle from "../../assets/logo-circle.jpeg";
import growthProfileFooter from "../../assets/growth-profile-footer.png";

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const currentYear = new Date().getFullYear();
  const servicesData = t('footer.ourStory.servicesList', { returnObjects: true });
  const services: string[] = Array.isArray(servicesData) ? servicesData : [];

  return (
    <footer className="relative">
      {/* Main Footer Section */}
      <div className="relative bg-black bg-opacity-90">
        {/* Background Image Layer */}
        <div
          className="absolute inset-0 z-0 opacity-20"
          style={{
            backgroundImage: `url(${hqWallpaper})`,
            backgroundSize: "cover",
            backgroundAttachment: "fixed",
            backgroundPosition: "center",
          }}
        ></div>

        {/* Background Pattern Overlay */}
        <div
          className="absolute inset-0 z-10 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>

        {/* Footer Content */}
        <div className="container mx-auto px-6 py-16 relative z-20">
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
            {/* Brand Section */}
            <div className="lg:col-span-1 flex flex-col items-center lg:items-start text-center lg:text-left">
              <div className="mb-6">
                <div className="relative">
                  <div className="w-28 h-28 overflow-hidden rounded-full shadow-lg border-2 border-white/20 flex items-center justify-center bg-white/5 backdrop-blur-sm transition-transform duration-300 hover:scale-105">
                    <img
                      src={logoCircle}
                      alt="Christ Reigns Logo"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <p className="text-white text-base font-bold tracking-wide uppercase leading-snug">
                {t("footer.tagline")}
              </p>
            </div>

            {/* Quick Links */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-bold text-white mb-6 uppercase tracking-wider">
                {t("footer.quickLinks.title")}
              </h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href="https://christreignslab.com/articles/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold block py-1"
                  >
                    {t("footer.quickLinks.articles")}
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.youtube.com/@christ-reigns-media-music"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold block py-1"
                  >
                    {t("footer.quickLinks.worship")}
                  </a>
                </li>
                <li>
                  <button
                    onClick={() =>
                      (window.location.href = "https://christreignsmedia.io/register")
                    }
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold text-left block py-1"
                  >
                    {t("footer.quickLinks.register")}
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      (window.location.href =
                        "https://christreignslab.com/about-us/")
                    }
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold text-left block py-1"
                  >
                    {t("footer.quickLinks.about")}
                  </button>
                </li>
                <li>
                  <Link
                    to="/software"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold text-left block py-1"
                  >
                    {t("footer.quickLinks.software")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/technology"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold text-left block py-1"
                  >
                    {t("footer.quickLinks.technology")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/bible-and-study-resources"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold text-left block py-1"
                  >
                    {t("footer.resources.title")}
                  </Link>
                </li>
                <li>
                  <a
                    href="https://christreignsmedia.io/growth"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold block py-1"
                  >
                    {t("footer.quickLinks.growth")}
                  </a>
                </li>
                <li>
                  <a
                    href="https://christreignsmedia.io/bible-and-ai"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold block py-1"
                  >
                    {t("footer.quickLinks.bibleChat")}
                  </a>
                </li>
                <li>
                  <a
                    href="https://christreignsmedia.io/book-library"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold block py-1"
                  >
                    {t("footer.quickLinks.bookLibrary")}
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.christreignsmedia.io/bible-study-progress"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold block py-1"
                  >
                    {t("footer.quickLinks.bibleProgress")}
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.christreignsmedia.io/favorite-verses"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold block py-1"
                  >
                    {t("footer.quickLinks.favoriteVerses")}
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.christreignsmedia.io/blogs"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold block py-1"
                  >
                    {t("footer.quickLinks.journalBlogs")}
                  </a>
                </li>
                <li>
                  <Link
                    to="/copyright-notice"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold text-left block py-1"
                  >
                    {t("footer.quickLinks.legal")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-gray-200 hover:text-sky-300 transition-colors duration-200 text-sm font-semibold text-left block py-1"
                  >
                    {t("privacyPolicyPage.title")}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Digital Technology Services */}
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <FaLaptopCode className="text-cyan-400 w-5 h-5 flex-shrink-0" />
                <h4 className="text-lg font-bold text-white uppercase tracking-wider">
                  {t("footer.digitalServices.title", { defaultValue: "Digital Technology Services" })}
                </h4>
              </div>

              <p className="text-gray-300 text-sm leading-relaxed mb-6">
                {t("footer.digitalServices.intro", {
                  defaultValue: "In addition to creating the Christ Reigns Lab software (link below) which we hope will lead people to Christ and learning more about him through theology, apologetics, evangelism, etc.. we are specialized as well in other digital technology services for the Lord such as :"
                })}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-3 bg-white/5 rounded-xl p-3 border border-white/10 hover:bg-white/10 hover:border-cyan-400/30 transition-all duration-200 group"
                  >
                    <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0 group-hover:scale-125 transition-transform" />
                    <span className="text-gray-200 text-sm font-medium group-hover:text-white transition-colors">
                      {service}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
                <p className="text-cyan-300/90 text-xs italic font-medium">
                  {t("footer.digitalServices.conclusion", {
                    defaultValue: "This is how we believe God can use the technology we have learned to serve His kingdom."
                  })}
                </p>
                <Link
                  to="/bible-and-study-resources"
                  className="inline-flex items-center space-x-1.5 text-xs font-semibold text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  
                </Link>
              </div>

              {/* Growth Profile Preview Link Image */}
              <div className="mt-5">
                <a
                  href="https://christreignslab.com/growth"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative block rounded-2xl overflow-hidden border border-white/15 bg-white/5 shadow-xl transition-all duration-300 hover:border-cyan-400/50 hover:shadow-cyan-500/20 hover:scale-[1.01]"
                >
                  <div className="relative aspect-[1024/500] w-full overflow-hidden">
                    <img
                      src={growthProfileFooter}
                      alt="Christian Growth Profile - Christ Reigns Media Software"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity"></div>
                    <div className="absolute bottom-0 inset-x-0 p-3 sm:p-4 flex items-center justify-between">
                      <div>
                        <span className="text-[11px] font-bold text-cyan-400 uppercase tracking-wider block">
                          Featured Platform
                        </span>
                        <span className="text-sm font-semibold text-white group-hover:text-cyan-300 transition-colors line-clamp-1">
                          Christ Reigns Lab
                        </span>
                      </div>
                      <span className="px-3 py-1 rounded-lg bg-cyan-500/20 text-cyan-300 text-xs font-bold border border-cyan-500/40 group-hover:bg-cyan-500 group-hover:text-black transition-all flex items-center space-x-1 flex-shrink-0 ml-2">
                        <span>Visit</span>
                        <span>→</span>
                      </span>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* About Our Ministry */}
            <div className="lg:col-span-1">
              <h4 className="text-lg font-bold text-white mb-6 uppercase tracking-wider">
                {t("footer.ourMinistry.title")}
              </h4>
              <div className="space-y-4">
                <a
                  href="https://christreignslab.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white hover:text-sky-300 transition-colors duration-200 text-sm font-semibold flex items-center space-x-2"
                >
                  <FaCross className="text-sky-400" />
                  <span>Christ Reigns Media</span>
                </a>
                <p className="text-gray-200 text-sm leading-relaxed">
                  {t("footer.ourMinistry.description")}
                </p>
                <div className="space-y-2 text-sm text-gray-300">
                  <div className="flex items-start space-x-2">
                    <span className="text-sky-400 mt-1">•</span>
                    <span>{t("footer.ourMinistry.point1")}</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-sky-400 mt-1">•</span>
                    <span>{t("footer.ourMinistry.point2")}</span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <span className="text-sky-400 mt-1">•</span>
                    <span>{t("footer.ourMinistry.point3")}</span>
                  </div>
                </div>
                <p className="text-gray-300 text-xs italic mt-2">
                  {t("footer.ourMinistry.tagline")}
                </p>

                {/* YouTube Channels */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <h5 className="text-white text-xs font-bold uppercase tracking-wider mb-3 block">
                    {t("footer.ourMinistry.youtubeChannels")}
                  </h5>
                  <div className="space-y-2">
                    <a
                      href="https://www.youtube.com/@ChristReignsM"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-300 transition-all duration-300 group"
                    >
                      <FaYoutube className="text-red-500 w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                      <span className="text-sm font-semibold tracking-wide">Christ Reigns</span>
                    </a>
                    <a
                      href="https://www.youtube.com/@ChristReignsM-Es"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-300 transition-all duration-300 group"
                    >
                      <FaYoutube className="text-red-500 w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                      <span className="text-sm font-semibold tracking-wide">Christ Reigns Español</span>
                    </a>
                    <a
                      href="https://www.youtube.com/@christ-reigns-media-music"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-300 transition-all duration-300 group"
                    >
                      <FaYoutube className="text-red-500 w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
                      <span className="text-sm font-semibold tracking-wide">Christ Reigns Media Music</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-white/20 mt-12 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="flex items-center space-x-4 mb-4 md:mb-0">
                <p className="text-gray-400 text-sm flex items-center">
                  {t("footer.madeWith")}{" "}
                  <FaHeart className="text-red-500 mx-1" />{" "}
                  {t("footer.forChristianCommunity")}
                </p>
              </div>

              <div className="flex items-center space-x-6">
                <p className="text-white text-sm">
                  {t("footer.copyright", { year: currentYear })}
                </p>
                <button
                  onClick={scrollToTop}
                  className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full p-2 transition-all duration-300 transform hover:scale-110"
                  aria-label="Scroll to top"
                >
                  <FaArrowUp className="text-white w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Strip */}
      <div className="bg-black py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-center md:text-left mb-3 md:mb-0">
              <p className="text-white font-semibold">
                {t("footer.readyTitle")}
              </p>
              <p className="text-orange-100 text-sm">
                {t("footer.readySubtitle")}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() =>
                  window.open(
                    "https://buymeacoffee.com/christreignsmedia",
                    "_blank"
                  )
                }
                className="bg-yellow-400 text-gray-800 hover:bg-yellow-300 px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 text-sm flex items-center justify-center space-x-2"
              >
                <span>☕</span>
                <span>{t("footer.supportUs")}</span>
              </button>
              <button
                onClick={() =>
                  (window.location.href = "https://christreignsmedia.io")
                }
                className="bg-white text-orange-600 hover:bg-gray-100 px-6 py-2 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 text-sm"
              >
                {t("footer.startJourney")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
